import { useLocalSearchParams } from 'expo-router'
import { useOrgBySlug } from '@/hooks/useOrgBySlug'
import { OrgAccessError } from '@/lib/errors'
import type { Organization, UserRole } from '@/lib/types'

interface UseCurrentOrgOptions {
    throwIfNotFound?: boolean
    onlySlug?: string | null
}

interface CurrentOrgResult {
    org: Organization
    role: UserRole
    isLoading: boolean
}

interface CurrentOrgResultOptional {
    org: Organization | null
    role: UserRole | null
    isLoading: boolean
}

export function useCurrentOrg(): CurrentOrgResult
export function useCurrentOrg(options: {
    throwIfNotFound: false
    onlySlug?: string | null
}): CurrentOrgResultOptional
export function useCurrentOrg(options: {
    throwIfNotFound?: true
    onlySlug?: string | null
}): CurrentOrgResult
export function useCurrentOrg(onlySlug: string | null): CurrentOrgResult
export function useCurrentOrg(
    optionsOrSlug?: UseCurrentOrgOptions | string | null
): CurrentOrgResultOptional {
    const params = useLocalSearchParams<{ orgSlug: string }>()

    const { throwIfNotFound, onlySlug } = parseOptions(optionsOrSlug)
    const orgSlug = params.orgSlug || onlySlug
    const { org, role, isLoading } = useOrgBySlug(orgSlug)

    if (isLoading) {
        return throwIfNotFound
            ? {
                  org: null as unknown as Organization,
                  role: null as unknown as UserRole,
                  isLoading: true,
              }
            : { org: null, role: null, isLoading: true }
    }

    if (throwIfNotFound && !orgSlug) {
        throw new OrgAccessError('Organization not found: no slug available')
    }

    if (throwIfNotFound && !org) {
        throw new OrgAccessError(`Organization not found or no access: ${orgSlug}`)
    }

    return { org: org ?? null, role: role ?? null, isLoading: false }
}

function parseOptions(optionsOrSlug?: UseCurrentOrgOptions | string | null) {
    if (typeof optionsOrSlug === 'string' || optionsOrSlug === null) {
        return { throwIfNotFound: true, onlySlug: optionsOrSlug }
    }
    return {
        throwIfNotFound: optionsOrSlug?.throwIfNotFound ?? true,
        onlySlug: optionsOrSlug?.onlySlug ?? null,
    }
}
